package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.auth.RemoteInspection
import br.com.htms.inspeccaocampo.data.AgregacaoInspecao
import br.com.htms.inspeccaocampo.data.AgregacaoRespostas
import br.com.htms.inspeccaocampo.data.FormJson
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.DashDatas
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.FiltroPeriodo
import br.com.htms.inspeccaocampo.ui.kit.FiltrosAtivos
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoColunas
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.PeriodoDash
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.SemDadosDash

private fun dataDe(r: RemoteInspection) = DashDatas.deMs(r.dataHorarioMs ?: r.createdAt)
private fun tipoDe(r: RemoteInspection) = FormTipo.fromId(r.formType).rotulo
private fun obraDe(r: RemoteInspection) = AgregacaoInspecao.obra(r.summaryObra)
private fun rotuloBrutoInspetor(r: RemoteInspection) =
    r.summaryTst.trim().ifBlank { r.username.orEmpty().ifBlank { "Não informado" } }

private val COR_C = Color(0xFF1E88E5)
private val COR_NC = Color(0xFFE53935)
private val COR_NA = Color(0xFF43A047)
private val COR_SEM = Color(0xFFFDD835)
private val COR_ALTA = Color(0xFFC62828)
private val COR_MEDIA = Color(0xFFEF6C00)
private val COR_BAIXA = Color(0xFF2E7D32)

/**
 * Admin consulta o servidor pelo período (sem o corte de 60 dias do histórico);
 * os demais usuários veem o próprio histórico recente.
 */
@Composable
fun DashboardInspecoes(controller: AppController) {
    val authUser by controller.authUser.collectAsState()
    val locais by controller.inspecoes.collectAsState()
    val recentes by controller.remoteInspections.collectAsState()
    val admin = authUser?.isAdmin == true

    var periodo by remember { mutableStateOf(if (admin) PeriodoDash.M12 else PeriodoDash.D30) }
    var tick by remember { mutableStateOf(0) }
    var carregando by remember { mutableStateOf(false) }
    var erro by remember { mutableStateOf<String?>(null) }
    var consulta by remember { mutableStateOf<List<RemoteInspection>?>(null) }
    var contagens by remember { mutableStateOf<Map<String, AgregacaoRespostas.Counts>>(emptyMap()) }
    var carregandoContagens by remember { mutableStateOf(false) }

    LaunchedEffect(admin, periodo, tick) {
        carregando = true
        if (admin) {
            val ini = DashDatas.inicio(periodo)
            AuthApi.listInspectionsForConsolidado(fromMs = ini?.let { DashDatas.msDe(it) }, toMs = null)
                .onSuccess { consulta = it; erro = null }
                .onFailure { erro = it.message ?: "Falha ao consultar as inspeções." }
        } else {
            controller.atualizarHistoricoRemoto()
        }
        carregando = false
    }

    val todas = (if (admin) consulta else recentes).orEmpty()
    val hoje = DashDatas.hoje()
    val noPeriodo = todas.filter { DashDatas.noPeriodo(dataDe(it), periodo, hoje) }

    LaunchedEffect(noPeriodo.map { it.id }, tick) {
        val ids = noPeriodo.filter { it.hasJson }.map { it.id }
        if (ids.isEmpty()) {
            contagens = emptyMap()
            return@LaunchedEffect
        }
        carregandoContagens = true
        val mapa = linkedMapOf<String, AgregacaoRespostas.Counts>()
        for (chunk in ids.chunked(60)) {
            AuthApi.fetchConsolidadoPayloads(chunk).onSuccess { rows ->
                rows.forEach { row ->
                    runCatching { FormJson.inspecaoFromJson(row.json) }
                        .getOrNull()
                        ?.let { mapa[row.id] = AgregacaoRespostas.of(it) }
                }
            }
        }
        contagens = mapa
        carregandoContagens = false
    }

    var fTipo by remember { mutableStateOf<String?>(null) }
    var fObra by remember { mutableStateOf<String?>(null) }
    var fInspetor by remember { mutableStateOf<String?>(null) }
    var fFaixa by remember { mutableStateOf<Int?>(null) }
    var fDiaSem by remember { mutableStateOf<String?>(null) }
    var fCrit by remember { mutableStateOf<String?>(null) }
    var fResp by remember { mutableStateOf<String?>(null) }

    val mapaResp = remember(noPeriodo) { AgregacaoInspecao.mapaResponsaveis(noPeriodo.map(::rotuloBrutoInspetor)) }
    fun inspetorDe(r: RemoteInspection) = AgregacaoInspecao.responsavel(rotuloBrutoInspetor(r), mapaResp)
    fun critDe(r: RemoteInspection): String? = contagens[r.id]?.let { AgregacaoRespostas.criticidade(it) }

    val faixas = remember(periodo, todas) { DashDatas.faixas(periodo, noPeriodo.minOfOrNull { dataDe(it) }, hoje) }
    val semFaixa = noPeriodo.filter {
        (fTipo == null || tipoDe(it) == fTipo) &&
            (fObra == null || obraDe(it) == fObra) &&
            (fInspetor == null || AgregacaoInspecao.responsavel(rotuloBrutoInspetor(it), mapaResp) == fInspetor) &&
            (fDiaSem == null || AgregacaoInspecao.diaSemana(dataDe(it)) == fDiaSem) &&
            (fCrit == null || critDe(it) == fCrit) &&
            (fResp == null || when (fResp) {
                "C / Sim" -> (contagens[it.id]?.c ?: 0) > 0
                "NC / Não" -> (contagens[it.id]?.nc ?: 0) > 0
                "NA / N/A" -> (contagens[it.id]?.na ?: 0) > 0
                "Sem resposta" -> (contagens[it.id]?.sem ?: 0) > 0
                else -> true
            })
    }
    val lista = semFaixa.filter { fFaixa == null || DashDatas.indice(faixas, dataDe(it)) == fFaixa }
    val cores = coresRanking(contagem(noPeriodo, ::tipoDe).map { it.first })
    val pendentesEnvio = locais.count { !it.enviadoRemoto }
    val dias = faixas.firstOrNull()?.let { f -> (DashDatas.msDe(hoje)!! - DashDatas.msDe(f.ini)!!) / 86_400_000L + 1 } ?: 1L
    val porSemana = if (fFaixa == null) lista.size * 7.0 / dias.coerceAtLeast(1) else null
    val rankingObras = contagem(lista, ::obraDe)
    val obraLider = rankingObras.firstOrNull()
    val pctLider = if (lista.isNotEmpty() && obraLider != null) obraLider.second * 100.0 / lista.size else null
    val ordemSemana = listOf("Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo")
    val porDiaSemana = ordemSemana.map { d ->
        d to lista.count { AgregacaoInspecao.diaSemana(dataDe(it)) == d }.toDouble()
    }.filter { it.second > 0 }

    val countsLista = lista.mapNotNull { contagens[it.id] }
    val somaC = countsLista.sumOf { it.c }
    val somaNc = countsLista.sumOf { it.nc }
    val somaNa = countsLista.sumOf { it.na }
    val somaSem = countsLista.sumOf { it.sem }
    val nAlta = lista.count { critDe(it) == "ALTA" }
    val nMedia = lista.count { critDe(it) == "MEDIA" }
    val nBaixa = lista.count { critDe(it) == "BAIXA" }
    val fatiasResp = listOf(
        FatiaDash("C / Sim", somaC.toDouble(), COR_C),
        FatiaDash("NC / Não", somaNc.toDouble(), COR_NC),
        FatiaDash("NA / N/A", somaNa.toDouble(), COR_NA),
        FatiaDash("Sem resposta", somaSem.toDouble(), COR_SEM)
    ).filter { it.valor > 0 }
    val rankingCrit = listOf(
        "ALTA" to nAlta.toDouble(),
        "MEDIA" to nMedia.toDouble(),
        "BAIXA" to nBaixa.toDouble()
    ).filter { it.second > 0 }
    val coresCrit = mapOf("ALTA" to COR_ALTA, "MEDIA" to COR_MEDIA, "BAIXA" to COR_BAIXA)

    val filtrosAtivos = buildList {
        fFaixa?.let { i -> faixas.getOrNull(i)?.let { add("Período: ${it.rotulo}" to { fFaixa = null }) } }
        fTipo?.let { add("Tipo: $it" to { fTipo = null }) }
        fObra?.let { add("Obra: $it" to { fObra = null }) }
        fInspetor?.let { add("Responsável: $it" to { fInspetor = null }) }
        fDiaSem?.let { add("Dia: $it" to { fDiaSem = null }) }
        fCrit?.let { add("Criticidade: $it" to { fCrit = null }) }
        fResp?.let { add("Resposta: $it" to { fResp = null }) }
    }

    PainelDashboard(
        titulo = "Dashboard — Inspeções e checklists",
        subtitulo = buildString {
            if (admin) append("${periodo.rotulo} · obras e responsáveis unidos pelo CC / nome")
            else append("Suas inspeções enviadas nos últimos 60 dias")
            if (carregandoContagens) append(" · carregando C/NC/NA…")
        },
        carregando = carregando,
        erro = erro,
        onAtualizar = { tick++ },
        camposCor = cores.keys.mapIndexed { i, k -> k to CoresDash.corLong(i) } + listOf(
            "Inspeções" to 0xFF1E5AA8,
            "Soma C" to 0xFF1E88E5,
            "Soma NC" to 0xFFE53935,
            "Soma NA" to 0xFF43A047,
            "Crit. ALTA" to 0xFFC62828,
            "Crit. MEDIA" to 0xFFEF6C00,
            "Crit. BAIXA" to 0xFF2E7D32
        ),
        filtros = {
            FiltroPeriodo(
                periodo, { periodo = it; fFaixa = null },
                if (admin) PeriodoDash.entries else listOf(PeriodoDash.D30, PeriodoDash.D90)
            )
            FiltrosAtivos(filtrosAtivos) {
                fTipo = null; fObra = null; fInspetor = null; fFaixa = null
                fDiaSem = null; fCrit = null; fResp = null
            }
        }
    ) { largo, grande ->
        if (todas.isEmpty() && !carregando) {
            SemDadosDash("Sem inspeções", "Nenhuma inspeção enviada no período.")
            return@PainelDashboard
        }
        PaineisDoDash("inspecoes", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(
                    largo,
                    listOf(
                        { m ->
                            KpiDash(
                                "Inspeções", FmtDash.qtd(lista.size), m,
                                legenda = porSemana?.let { "média de ${FmtDash.num(it, 1)} por semana" } ?: ""
                            )
                        },
                        { m -> KpiDash("Soma C", FmtDash.qtd(somaC), m, cor = COR_C, legenda = "conforme / sim") },
                        { m -> KpiDash("Soma NC", FmtDash.qtd(somaNc), m, cor = COR_NC, legenda = "não conforme / não") },
                        { m -> KpiDash("Soma NA", FmtDash.qtd(somaNa), m, cor = COR_NA, legenda = "não se aplica") },
                        { m -> KpiDash("Crit. ALTA", FmtDash.qtd(nAlta), m, cor = COR_ALTA, destaque = nAlta > 0) },
                        { m -> KpiDash("Crit. MEDIA", FmtDash.qtd(nMedia), m, cor = COR_MEDIA) },
                        { m -> KpiDash("Crit. BAIXA", FmtDash.qtd(nBaixa), m, cor = COR_BAIXA) },
                        { m ->
                            KpiDash(
                                "Pendentes de envio", FmtDash.qtd(pendentesEnvio), m,
                                cor = if (pendentesEnvio > 0) CoresDash.atencao else CoresDash.bom,
                                legenda = "salvas neste aparelho", destaque = pendentesEnvio > 0
                            )
                        },
                        { m ->
                            KpiDash(
                                "Obra líder",
                                obraLider?.let { FmtDash.pct((pctLider ?: 0.0) / 100.0, 0) } ?: "—",
                                m,
                                cor = CoresDash.cor(2),
                                legenda = obraLider?.first?.take(42) ?: "sem dados"
                            )
                        }
                    )
                )
            }),
            ItemPainel("periodo", "Inspeções por período", inteiro = true) { m ->
                GraficoColunas(
                    "Inspeções por período", "Empilhadas por tipo · clique numa coluna para filtrar",
                    faixas.map { it.rotulo },
                    seriesPorChave(semFaixa, faixas, ::dataDe, ::tipoDe, cores),
                    m, grande, fmt = { FmtDash.qtd(it) },
                    selecionada = fFaixa, onClick = { fFaixa = if (fFaixa == it) null else it }
                )
            },
            ItemPainel("respostas", "Distribuição C / NC / NA") { m ->
                GraficoRosca(
                    "Distribuição C / NC / NA",
                    "Soma dos itens respondidos · clique para filtrar inspeções com essa resposta",
                    fatiasResp, m, grande,
                    rotuloTotal = "itens", selecionada = fResp,
                    onClick = { fResp = alternar(fResp, it) }
                )
            },
            ItemPainel("criticidade", "Por criticidade") { m ->
                RankingBarras(
                    "Por criticidade",
                    "ALTA ≥3 NC ou ≥15% · MEDIA com NC · BAIXA sem NC · clique para filtrar",
                    rankingCrit, m, cor = COR_ALTA,
                    selecionado = fCrit,
                    onClick = { fCrit = alternar(fCrit, it) },
                    corItem = { coresCrit[it] ?: COR_BAIXA }
                )
            },
            ItemPainel("tipo", "Por tipo de formulário") { m ->
                GraficoRosca(
                    "Por tipo de formulário", "Clique para filtrar",
                    fatias(contagem(lista, ::tipoDe), cores), m, grande,
                    rotuloTotal = "inspeções", selecionada = fTipo,
                    onClick = { fTipo = alternar(fTipo, it) }
                )
            },
            ItemPainel("obra", "Por obra / CC") { m ->
                RankingBarras(
                    "Por obra / CC",
                    "Nomes antigos unidos ao CC do catálogo · clique para filtrar",
                    rankingObras, m, selecionado = fObra,
                    onClick = { fObra = alternar(fObra, it) }
                )
            },
            ItemPainel("responsavel", "Por responsável") { m ->
                RankingBarras(
                    "Por responsável",
                    "Login e nome completo unidos · clique para filtrar",
                    contagem(lista) { inspetorDe(it) }, m, cor = CoresDash.cor(1),
                    selecionado = fInspetor,
                    onClick = { fInspetor = alternar(fInspetor, it) }
                )
            },
            ItemPainel("diasem", "Por dia da semana") { m ->
                RankingBarras(
                    "Por dia da semana",
                    "Quando as inspeções foram feitas · clique para filtrar",
                    porDiaSemana, m, cor = CoresDash.cor(4),
                    selecionado = fDiaSem,
                    onClick = { fDiaSem = alternar(fDiaSem, it) }
                )
            }
        ))
    }
}
